import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, PencilSimple, X } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { canEditItems } from "@/lib/roles";

const DIET_TYPES = ["Wellness", "Vegetarian", "Bland", "ESR", "CKD3", "Pescatarian", "GDM", "Well+"];
const DIET_CODES = Array.from({ length: 5 }, (_, w) =>
  Array.from({ length: 7 }, (_, p) => `${w + 1}.${p + 1}`)
).flat();

export default function MealItemDetail() {
  const { mealId } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const canEdit = canEditItems(user);
  const openEditFromUrl = sp.get("edit") === "1";

  const [meal, setMeal] = useState(null);
  const [editing, setEditing] = useState(openEditFromUrl && canEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    diet_type: "",
    diet_code: "",
    cycle_week: "",
    cycle_position: "",
    location: "",
    cost: "",
    par_level: "",
    active: true,
  });

  useEffect(() => {
    api.get(`/meals/${mealId}`)
      .then((r) => {
        setMeal(r.data);
        setForm({
          diet_type: r.data.diet_type || "",
          diet_code: r.data.diet_code || "",
          cycle_week: r.data.cycle_week ?? "",
          cycle_position: r.data.cycle_position ?? "",
          location: r.data.location || "",
          cost: r.data.cost === null || r.data.cost === undefined ? "" : r.data.cost,
          par_level: r.data.par_level ?? "",
          active: r.data.active !== false,
        });
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [mealId]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!DIET_TYPES.includes(form.diet_type)) return toast.error("Select a valid Diet Type");
    if (!DIET_CODES.includes(form.diet_code)) return toast.error("Select a valid Diet Code");
    const cw = Number(form.cycle_week);
    const cp = Number(form.cycle_position);
    if (!Number.isInteger(cw) || cw < 1 || cw > 5) return toast.error("Cycle Week must be 1–5");
    if (!Number.isInteger(cp) || cp < 1 || cp > 7) return toast.error("Position must be 1–7");
    let costPayload = null;
    if (form.cost !== "" && form.cost !== null && form.cost !== undefined) {
      const c = Number(form.cost);
      if (!Number.isFinite(c)) return toast.error("Cost must be a valid number");
      if (c < 0) return toast.error("Cost cannot be negative");
      costPayload = Math.round(c * 10000) / 10000;
    }
    let parPayload = null;
    if (form.par_level !== "" && form.par_level !== null && form.par_level !== undefined) {
      const p = Number(form.par_level);
      if (!Number.isFinite(p) || p < 0) return toast.error("PAR must be a non-negative number");
      parPayload = p;
    }
    setSaving(true);
    try {
      const payload = {
        diet_type: form.diet_type,
        diet_code: form.diet_code,
        cycle_week: cw,
        cycle_position: cp,
        location: String(form.location || "").trim(),
        cost: costPayload,
        active: !!form.active,
      };
      if (parPayload !== null) payload.par_level = parPayload;
      await api.patch(`/meals/${mealId}`, payload);
      toast.success("Meal item saved");
      const r = await api.get(`/meals/${mealId}`);
      setMeal(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!meal) return <div className="p-6 text-sm text-muted-foreground font-mono">Loading meal item…</div>;

  const costDisplay = meal.cost === null || meal.cost === undefined || meal.cost === ""
    ? <span className="italic text-muted-foreground">Not Set</span>
    : `$${Number(meal.cost).toFixed(4)} / meal`;

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <Link to="/meal-items" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft size={14} /> Back to Meal Items
      </Link>

      <div className="grid md:grid-cols-[2fr_1fr] gap-4">
        <div>
          <div className="card-flat border-border p-4 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="label-tech mb-1">Meal Record</div>
                <h1 data-testid="meal-item-detail-name" className="font-display font-black text-2xl md:text-3xl tracking-tight break-words">
                  {meal.diet_type} · {meal.diet_code}
                </h1>
                <div className="text-xs font-mono text-muted-foreground mt-2 space-y-0.5">
                  <div>RECORD · {meal.meal_record_id}</div>
                  <div>QR · {meal.qr_value || `MR-${meal.meal_record_id}`}</div>
                  <div>WEEK {meal.cycle_week ?? "—"} · POS {meal.cycle_position ?? "—"}</div>
                </div>
              </div>
              <div className="shrink-0 border border-border p-2 bg-white">
                <img
                  data-testid="meal-item-detail-qr"
                  alt="QR"
                  src={`${BACKEND_URL}/api/qr/png/${encodeURIComponent(meal.qr_value || meal.meal_record_id)}?size=180`}
                  className="w-24 h-24 md:w-28 md:h-28"
                />
                <div className="text-[9px] font-mono text-center mt-1 text-black">{meal.qr_value || meal.meal_record_id}</div>
              </div>
            </div>
          </div>

          {canEdit && !editing && (
            <div className="flex justify-end mb-3">
              <button
                data-testid="edit-meal-btn"
                onClick={() => setEditing(true)}
                className="btn-hard h-10 px-4 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600 flex items-center gap-2"
              >
                <PencilSimple size={14} weight="bold" /> Edit Meal Details
              </button>
            </div>
          )}

          {editing && canEdit && (
            <form
              onSubmit={(e) => { e.preventDefault(); save(); }}
              data-testid="meal-edit-form"
              className="card-flat border-2 border-primary p-4 mb-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="label-tech">Edit Meal Details</div>
                <button type="button" onClick={() => setEditing(false)} className="text-xs text-muted-foreground hover:text-foreground">
                  <X size={14} weight="bold" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs">
                  <div className="label-tech mb-1">Diet Type</div>
                  <select data-testid="edit-meal-diet-type" value={form.diet_type} onChange={(e) => setF("diet_type", e.target.value)}
                    className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm">
                    {DIET_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                  </select>
                </label>
                <label className="text-xs">
                  <div className="label-tech mb-1">Diet Code</div>
                  <select data-testid="edit-meal-diet-code" value={form.diet_code} onChange={(e) => {
                    const dc = e.target.value;
                    const [w, p] = dc.split(".");
                    setForm((s) => ({ ...s, diet_code: dc, cycle_week: Number(w), cycle_position: Number(p) }));
                  }} className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm font-mono">
                    {DIET_CODES.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </label>
                <label className="text-xs">
                  <div className="label-tech mb-1">Cycle Week</div>
                  <input data-testid="edit-meal-cycle-week" type="number" min="1" max="5" value={form.cycle_week}
                    onChange={(e) => setF("cycle_week", e.target.value)}
                    className="w-full h-10 px-2 border border-border bg-background rounded-sm font-mono text-sm" />
                </label>
                <label className="text-xs">
                  <div className="label-tech mb-1">Position</div>
                  <input data-testid="edit-meal-position" type="number" min="1" max="7" value={form.cycle_position}
                    onChange={(e) => setF("cycle_position", e.target.value)}
                    className="w-full h-10 px-2 border border-border bg-background rounded-sm font-mono text-sm" />
                </label>
                <label className="text-xs">
                  <div className="label-tech mb-1">Location</div>
                  <input data-testid="edit-meal-location" value={form.location} onChange={(e) => setF("location", e.target.value)}
                    className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm" />
                </label>
                <label className="text-xs">
                  <div className="label-tech mb-1">PAR Level (meals)</div>
                  <input data-testid="edit-meal-par" type="number" min="0" step="1" value={form.par_level}
                    onChange={(e) => setF("par_level", e.target.value)}
                    className="w-full h-10 px-2 border border-border bg-background rounded-sm font-mono text-sm" />
                </label>
                <label className="text-xs md:col-span-2">
                  <div className="label-tech mb-1">Cost <span className="text-muted-foreground normal-case ml-2">(per individual meal)</span></div>
                  <div className="relative">
                    <span className="absolute left-2 top-2.5 text-muted-foreground text-sm font-mono">$</span>
                    <input data-testid="edit-meal-cost" type="number" min="0" step="0.0001" value={form.cost}
                      onChange={(e) => setF("cost", e.target.value)}
                      placeholder="(leave blank for Not Set)"
                      className="w-full h-10 pl-6 pr-2 border border-border bg-background rounded-sm font-mono text-sm" />
                  </div>
                </label>
                <label className="text-xs md:col-span-2 flex items-center gap-2">
                  <input data-testid="edit-meal-active" type="checkbox" checked={!!form.active}
                    onChange={(e) => setF("active", e.target.checked)} />
                  <span className="label-tech">Active</span>
                </label>
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" data-testid="edit-meal-save" disabled={saving}
                  className="btn-hard h-10 px-4 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1">
                  <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(false)}
                  className="btn-hard h-10 px-4 border border-border text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-muted">
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="card-flat border-border p-4">
            <div className="label-tech mb-3">Current State</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="label-tech mb-1">On Hand</div>
                <div className="font-display font-black text-2xl" data-testid="meal-current-meals">{Number(meal.current_meals || 0).toFixed(0)}</div>
                <div className="text-[10px] text-muted-foreground">meals</div>
              </div>
              <div>
                <div className="label-tech mb-1">PAR</div>
                <div className="font-display font-black text-2xl">{Number(meal.par_level || 0).toFixed(0)}</div>
              </div>
              <div>
                <div className="label-tech mb-1">Status</div>
                <div className="font-display font-bold text-sm mt-1">
                  {meal.active === false ? "Inactive" : "Active"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-flat border-border p-4">
            <div className="label-tech mb-2">Meal Details</div>
            <div className="text-sm space-y-1 font-mono">
              <div className="flex justify-between"><span className="text-muted-foreground">Diet Type</span><span>{meal.diet_type}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Diet Code</span><span>{meal.diet_code}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Cycle Week</span><span>{meal.cycle_week ?? "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Position</span><span>{meal.cycle_position ?? "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Location</span><span>{meal.location || "—"}</span></div>
              <div className="flex justify-between" data-testid="meal-cost-view">
                <span className="text-muted-foreground">Cost <span className="text-[10px]">(per meal)</span></span>
                <span className="font-mono">{costDisplay}</span>
              </div>
            </div>
          </div>
          <div className="card-flat border-border p-4">
            <div className="label-tech mb-2">Conversions</div>
            <div className="text-sm space-y-1 font-mono">
              {Object.entries(meal.conversions || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-muted-foreground uppercase">{k.replace("_", " ")}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
